import { z } from 'zod';
import { DEFAULT_PAGE_LIMIT, MAX_PAGE_LIMIT } from '../constants.js';
// Hex7 color validator (#RRGGBB). [D-09]
export const HexColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex color like #RRGGBB');
export const PaginationQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_PAGE_LIMIT)
        .default(DEFAULT_PAGE_LIMIT),
    /**
     * Offset-based pagination for table UIs that want to render a page number
     * widget (e.g. "1 / 12, jump to 5"). Cursor-pagination is more efficient
     * for infinite-scroll consumers; offset is acceptable for the
     * 500-item-cap admin tables where the count() is cheap. Default 0.
     */
    offset: z.coerce.number().int().min(0).optional(),
});
export const ResourcePermissionsSchema = z.object({
    ownerId: z.string().nullable().optional(),
    view: z.object({ userIds: z.array(z.string()), groupIds: z.array(z.string()) }),
    edit: z.object({ userIds: z.array(z.string()), groupIds: z.array(z.string()) }),
});
/**
 * Coerces query-string values into a string array.
 *
 * Fastify parses repeated query params (`?status=todo&status=done`) as an array,
 * but a single value (`?status=todo`) arrives as a string. Both should validate
 * as `string[]`. Wrap your array schemas with this helper.
 *
 *   status: stringArrayQuery(z.string()).optional()
 *   priority: stringArrayQuery(WorkPrioritySchema).optional()
 */
export function stringArrayQuery(item) {
    return z.preprocess((v) => {
        if (v === undefined || v === null)
            return undefined;
        if (Array.isArray(v))
            return v;
        if (typeof v === 'string' && v.includes(','))
            return v.split(',').map((s) => s.trim()).filter(Boolean);
        return [v];
    }, z.array(item));
}
/**
 * Properly coerces a query-string boolean. `z.coerce.boolean()` is wrong for
 * URLs — it uses `Boolean(v)`, which treats any non-empty string (including
 * `"false"`) as `true`. This helper handles the string forms users actually
 * type: "true"/"false", "1"/"0", "yes"/"no", "on"/"off", "" (empty = false).
 */
export const BooleanQuerySchema = z.preprocess((v) => {
    if (typeof v === 'boolean')
        return v;
    if (v === undefined || v === null)
        return undefined;
    const s = String(v).toLowerCase().trim();
    if (['true', '1', 'yes', 'on'].includes(s))
        return true;
    if (['false', '0', 'no', 'off', ''].includes(s))
        return false;
    return v; // let Zod reject anything else
}, z.boolean());
/**
 * Shared `includeDeleted` / `deletedOnly` query fields for the 6 soft-deletable
 * resource list endpoints (CTSK-388, Phase 2 of CTSK-385).
 *
 *   - default (both false/undefined): live rows only (`deleted_at IS NULL`)
 *   - includeDeleted=true: live + soft-deleted rows, no filter
 *   - deletedOnly=true: soft-deleted rows only (`deleted_at IS NOT NULL`)
 *
 * Spread into a resource's list-query `z.object({...})` shape, then chain
 * `.superRefine(refineDeletedFilterMutualExclusion)` on the *final* schema
 * (after any `.extend()` calls — ZodEffects from superRefine can't be
 * extended further, so this must run last).
 *
 * Uses `BooleanQuerySchema` (not `z.coerce.boolean()` per the ticket brief's
 * literal suggestion) — `z.coerce.boolean()` treats the string `"false"` as
 * `true`, which would be a live bug for a delete-visibility toggle.
 */
export const deletedFilterFields = {
    includeDeleted: BooleanQuerySchema.optional(),
    deletedOnly: BooleanQuerySchema.optional(),
};
export function refineDeletedFilterMutualExclusion(data, ctx) {
    if (data.includeDeleted && data.deletedOnly) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'includeDeleted and deletedOnly are mutually exclusive — pass at most one.',
            path: ['deletedOnly'],
        });
    }
}
/**
 * Body shape for the 6 `POST /<resource>/:id/actions/purge` hard-delete
 * endpoints (CTSK-333, Phase 3 of CTSK-385). The route handler compares
 * `confirmSlug` against the target row's own natural key (slug for
 * containers/tools/nests, name for tags, key for custom properties,
 * title for documents — documents have no literal "slug" column) and
 * 422s on mismatch. This schema only validates shape (non-empty string);
 * the actual match check is per-route since the field being compared
 * against differs by resource.
 */
export const PurgeConfirmSchema = z.object({
    confirmSlug: z.string().min(1, 'confirmSlug is required'),
});
//# sourceMappingURL=common.js.map